import React, { useContext, useEffect } from "react";
import GlobalConfig from "../../context/GlobalConfig";
import JourneyConfig from "../../context/JourneyConfig";
import Head from "../Head";
import { languageData } from "../../constants/languageData";
import RenderSvg from "../../components/RenderSvg";
import LivenessCheckError from "./LivenessCheckError";
import GenericError from "./GenericError";
import FaceCheckError from "./FaceCheckError";


function ErrorHandler({ onNextStep, currentStep, stepperFunc, error, setError }) {
  const { config } = useContext(GlobalConfig);
  let { branding } = config ?? {};
  const language = sessionStorage.getItem("language") ?? "en";
  if (!branding) {
    const brandingFromStorage = sessionStorage.getItem("branding");
    if (brandingFromStorage) {
      try {
        branding = JSON.parse(brandingFromStorage);
      } catch (e) {
        // If parsing fails, use default branding
        branding = null;
      }
    }
  }
  const color = config?.branding?.accentColor ?? "#1658aa";

  const errorCategory = sessionStorage.getItem("errorCategory");
  const isLivenessError = errorCategory === "livenessCheckError";
  let isFaceCheckError = false;
  let isMultipleFacesFound = false;
  let isNoFaceFound = false;
  let isActiveLivenessCheckFailed = false;
  let errorHandlerTexts = languageData[language]?.errorHandler || {
    heading: "Oops! Something went wrong.",
    subHeading1: "We're sorry, but an error has occurred.",
    subHeading2: "Please try again later.",
    button: "Try again",
  };


  if (isLivenessError) {
    let errorReason = null;
    try {
      const errorReasonStr = sessionStorage.getItem("errorReason");
      if (errorReasonStr) {
        errorReason = JSON.parse(errorReasonStr);
      }
    } catch (error) {
      console.error('Error parsing errorReason from sessionStorage:', error);
      // Fall back to default error handling
    }
    const faceDetections = errorReason?.faceDetections;
    const isFaceCovered = errorReason?.isFaceCovered;
    isActiveLivenessCheckFailed = errorReason?.activeLivenessCheckFailed;
    // Safely compute the multiple faces flag from the backend message (case-insensitive)
    if(errorReason?.faceCountMessage === "multiplefaces"){
        isMultipleFacesFound = true;
    }
    else if(errorReason?.faceCountMessage === "no faces"){
        isNoFaceFound = true;
    }

    if (isFaceCovered && faceDetections?.length > 0) {
      const errorHandlerTemplate =
        languageData[language]?.[errorCategory]?.["faceDetections"] ??
        languageData["en"]?.[errorCategory]?.["faceDetections"];

      const formatDetections = (items = []) => {
        if (items.length === 1) return items[0];
        if (items.length === 2) return items.join(" and ");
        return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
      };

      const detectionText = formatDetections(faceDetections);
      errorHandlerTexts = {
        heading: `${errorHandlerTemplate?.heading} ${detectionText}`,
        subHeading1: "",
        subHeading2: "",
        button: languageData[language]?.errorHandler?.button ?? "Try Again",
      };
    } else if (isFaceCovered) {
      errorHandlerTexts =
        languageData[language]?.[errorCategory]?.["isFaceCovered"] ??
        languageData["en"]?.[errorCategory]?.["isFaceCovered"];
    } else if (isMultipleFacesFound) {
      errorHandlerTexts =
        languageData[language]?.[errorCategory]?.["isMultipleFacesFound"] ??
        languageData["en"]?.[errorCategory]?.["isMultipleFacesFound"];
    } else if (isActiveLivenessCheckFailed){
      const activeLivenessMessageKey =
        config?.faceCapture?.activeLivenessType === "OTP"
          ? "activeLivenessOtpFailureMessage"
          : "activeLivenessGenericFailureMessage";
      errorHandlerTexts =
        languageData[language]?.[errorCategory]?.[activeLivenessMessageKey] ??
        languageData["en"]?.[errorCategory]?.[activeLivenessMessageKey];
    }else if(isNoFaceFound){
      errorHandlerTexts =
        languageData[language]?.[errorCategory]?.["noFacesFound"] ??
        languageData["en"]?.[errorCategory]?.["noFacesFound"];
    }else {
      isFaceCheckError = true;
      errorHandlerTexts =
        languageData[language]?.[errorCategory]?.["selfieError"] ??
        languageData["en"]?.[errorCategory]?.["selfieError"];
    }
  }

  return (
    <>
      {(isLivenessError && !isFaceCheckError) || isMultipleFacesFound || isActiveLivenessCheckFailed ? (
        <FaceCheckError
          stepperFunc={stepperFunc}
          onNextStep={onNextStep}
          color={color}
          errorHandlerTexts={errorHandlerTexts}
          config={config}
        />
      ) : isLivenessError && isFaceCheckError ? (
        <LivenessCheckError
          stepperFunc={stepperFunc}
          onNextStep={onNextStep}
          color={color}
          errorHandlerTexts={errorHandlerTexts}
          config={config}
        />
      ) : (
        <GenericError
          stepperFunc={stepperFunc}
          onNextStep={onNextStep}
          color={color}
          errorHandlerTexts={errorHandlerTexts}
          config={config}
        />
      )}
    </>
  );
}

export default ErrorHandler;